import { useCallback, useMemo } from "react";
import { AgentUsageViewSchema } from "@common/types/views/agentUsageView";
import { View } from "@common/types/views/views";
import { CalendarIcon } from "@icons";
import { useOrganizationAgentUsage } from "@api/agentUsage.api";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useOrganizationClients } from "@components/organizationView/activity/useOrganizationClients";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { useAgentUsageViewStore } from "@hooks/view/useAgentUsageViewStore";
import { filterUsageRows, toAgentUsageRows } from "./agentUsageRow";
import { agentUsageViewConfig } from "./agentUsageViewConfig";
import { AgentUsageTimeline } from "./timeline/AgentUsageTimeline";
import { useAgentUsageFilters } from "./useAgentUsageFilters";
import styles from "./AgentUsagePage.module.scss";

const AgentUsagePageContent = () => {
    const organizationId = useActiveOrganization()?.id ?? "";
    const { data: workspaces } = useOrganizationClients();
    const { view, setView } = useAgentUsageViewStore();
    const {
        monthly,
        historyStart,
        today,
        selection,
        window,
        tableQuery,
        search,
        setSearch,
        previewWindow,
        setPreviewWindow,
        handlePickerChange,
        handleWindowChange,
    } = useAgentUsageFilters(organizationId);
    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_AGENT_USAGE_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });
    const usage = useOrganizationAgentUsage({
        organizationId,
        query: tableQuery,
    });

    const rows = useMemo(
        () =>
            filterUsageRows(
                toAgentUsageRows({
                    clients: usage.data?.clients ?? [],
                    workspaces: workspaces ?? [],
                }),
                search,
            ),
        [usage.data, workspaces, search],
    );

    const handleViewChange = useCallback(
        (nextView: View) => {
            const parsed = AgentUsageViewSchema.safeParse(nextView);
            if (parsed.success) {
                setView(parsed.data);
            }
        },
        [setView],
    );

    const emptyView = useCallback(
        () =>
            search ? (
                <EmptyState
                    header="No clients match"
                    ctaText="Clear search"
                    action={() => setSearch(undefined)}
                />
            ) : (
                <EmptyState header="No usage yet" ctaText={undefined} />
            ),
        [search, setSearch],
    );

    if (usage.data === null) {
        return (
            <EmptyState
                header="Agent usage isn't available yet"
                body="Usage shows up once Kick runs tasks for one of your clients."
                ctaText={undefined}
            />
        );
    }

    return (
        <main className={styles.container}>
            <TablePageHeader
                isFullPage
                borderBottom
                filterContainerClassName={styles.timelineToolbar}
                search={search}
                searchPlaceholder="Search clients"
                onSearchChange={(value) => setSearch(value || undefined)}
                prependChildren={
                    <DateRangeFilterNext
                        dateRange={selection}
                        onChange={handlePickerChange}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                        previewRange={previewWindow}
                        leftIcon={<CalendarIcon />}
                    />
                }
            />
            <AgentUsageTimeline
                months={monthly.data?.months ?? []}
                isLoading={monthly.isLoading}
                historyStart={historyStart}
                today={today}
                window={window}
                onWindowChange={handleWindowChange}
                onWindowPreview={setPreviewWindow}
            />
            <ViewTable
                data={usage.isLoading && !usage.data ? null : rows}
                view={view}
                config={agentUsageViewConfig}
                className={styles.timelineTable}
                onViewChange={handleViewChange}
                enableColumnResizing
                fixedTopOffsetPx
                emptyView={emptyView}
            />
        </main>
    );
};

/** Waits for the firm's clients so the first chart and rows already carry their names and history start. */
export const AgentUsagePage = () => {
    const { isLoading } = useOrganizationClients();
    if (isLoading) {
        return <Loader />;
    }
    return <AgentUsagePageContent />;
};
